import React, { useState, useEffect, useCallback } from 'react';
import { CalendarRange, Plus, Trash2, Save, ChevronRight, ListChecks } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const BASE = '/api/parent-app/admin';

type Year        = { id: number; year_label: string; is_active: number };
type Curriculum  = { id: number; name: string; board_id: number };
type Term        = { id: number; name: string; sequence: number; weightage: string | number | null; start_date: string | null; end_date: string | null; exam_count: number; academic_year_id_fk: number; curriculum_id_fk: number | null };
type Exam        = { id: number; name: string; exam_type: string; term_id_fk: number; class_id_fk: number | null; class_name: string | null; weightage: string | number | null; start_date: string | null; end_date: string | null; subject_count: number; skill_count: number };
type ClassRow    = { id: number; name?: string; class_name?: string; display_name?: string };
type ExamSubject = { subject_id: number; name: string; included: boolean; exam_subject_id: number | null; max_marks: string | number | null; passing_marks: string | number | null; exam_date: string | null };
type ExamSkill   = { skill_id: number; name: string; included: boolean };

const EXAM_TYPES = [
  ['formative', 'Formative'], ['summative', 'Summative'],
  ['unit_test', 'Unit Test'], ['mid_term', 'Mid-Term'], ['final', 'Final'],
  ['practical', 'Practical'], ['oral', 'Oral'], ['other', 'Other'],
] as const;

export default function ExamsPage() {
  const [years, setYears]               = useState<Year[]>([]);
  const [yearId, setYearId]             = useState<number | ''>('');
  const [curriculums, setCurriculums]   = useState<Curriculum[]>([]);
  const [curriculumId, setCurriculumId] = useState<number | null>(null);
  const [terms, setTerms]               = useState<Term[]>([]);
  const [termId, setTermId]             = useState<number | null>(null);
  const [classes, setClasses]           = useState<ClassRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showTermModal, setShowTermModal] = useState(false);
  const [tName, setTName]               = useState('');
  const [tWeight, setTWeight]           = useState('');

  const loadTerms = useCallback((yr: number, currId: number | null) => {
    const params = new URLSearchParams({ academic_year_id: String(yr) });
    if (currId) params.set('curriculum_id', String(currId));
    apiRequest<any>(`${BASE}/exam-terms?${params}`)
      .then(res => setTerms(res.data || []))
      .catch(() => toastMessage('Failed to load terms', 'error'));
  }, []);

  useEffect(() => {
    Promise.all([
      apiRequest<any>(`/api/parent-app/academic-years`).then(r => r.data || {}).catch(() => ({})),
      apiRequest<any>(`${BASE}/curriculum/hierarchy`).then(r => r.data || []).catch(() => []),
      apiRequest<any>(`${BASE}/classes`).then(r => flattenClasses(r.data)).catch(() => []),
    ]).then(([yData, currData, cs]) => {
      const ys: Year[] = Array.isArray(yData) ? yData : (yData.years || []);
      const currs: Curriculum[] = (currData || []).map((c: any) => ({ id: c.id, name: c.name, board_id: c.board_id }));
      setYears(ys);
      setCurriculums(currs);
      setClasses(cs);
      const active = ys.find((y: Year) => y.id === yData.activeYearId) || ys[0];
      const firstCurr = currs[0] ?? null;
      if (active) {
        setYearId(active.id);
        setCurriculumId(firstCurr?.id ?? null);
        loadTerms(active.id, firstCurr?.id ?? null);
      }
    }).finally(() => setLoading(false));
  }, [loadTerms]);

  const onYear = (id: number) => { setYearId(id); setTermId(null); loadTerms(id, curriculumId); };
  const onCurriculum = (id: number) => { setCurriculumId(id); setTermId(null); if (yearId) loadTerms(yearId as number, id); };

  const openTermModal = () => { setTName(''); setTWeight(''); setShowTermModal(true); };

  const addTerm = async () => {
    if (!yearId) return toastMessage('Pick an academic year', 'error');
    if (!tName.trim()) return toastMessage('Enter a term name', 'error');
    try {
      const r = await apiRequest(`${BASE}/exam-terms`, {
        method: 'POST',
        body: JSON.stringify({
          academic_year_id_fk: yearId,
          curriculum_id_fk: curriculumId || null,
          name: tName.trim(),
          weightage: tWeight ? Number(tWeight) : null,
          sequence: terms.length,
        }),
      });
      // apiRequest resolves (doesn't throw) on HTTP error responses — must
      // check success explicitly or a rejected request looks identical to one
      // that succeeded.
      if (!r.success) { toastMessage(r.message || 'Could not add term', 'error'); return; }
      setShowTermModal(false);
      loadTerms(yearId as number, curriculumId);
      toastMessage('Term added', 'success');
    } catch { toastMessage('Could not add term', 'error'); }
  };

  const delTerm = async (id: number) => {
    if (!confirm('Delete this term?')) return;
    try {
      const r = await apiRequest(`${BASE}/exam-terms/${id}`, { method: 'DELETE' });
      if (!r.success) { toastMessage(r.message || 'Term has exams — delete those first', 'error'); return; }
      if (termId === id) setTermId(null);
      loadTerms(yearId as number, curriculumId);
    } catch { toastMessage('Term has exams — delete those first', 'error'); }
  };

  if (loading) return <p style={{ padding: 20, color: 'var(--text-2)' }}>Loading…</p>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div className="page-head">
        <div className="page-head-icon"><CalendarRange size={24} style={{ color: '#fff' }} /></div>
        <div className="page-head-text">
          <h1>Exams</h1>
          <p>Board-wise terms and exams are auto-created when the academic year is activated</p>
        </div>
        <div className="page-head-actions">
          <select className="select" value={yearId} onChange={e => onYear(Number(e.target.value))}>
            {years.map(y => <option key={y.id} value={y.id}>{y.year_label}</option>)}
          </select>
        </div>
      </div>

      {/* Curriculum tabs */}
      {curriculums.length > 1 && (
        <div style={{ display: 'flex', gap: 4, padding: '0 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          {curriculums.map(c => (
            <button
              key={c.id}
              onClick={() => onCurriculum(c.id)}
              style={{
                padding: '10px 18px', fontSize: 13, fontWeight: 700,
                border: 'none',
                borderBottom: curriculumId === c.id ? '2px solid var(--slate)' : '2px solid transparent',
                background: 'transparent',
                color: curriculumId === c.id ? 'var(--text)' : 'var(--text-2)',
                cursor: 'pointer', transition: 'all .14s',
              }}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Terms sidebar */}
        <nav className="side-menu" style={{ width: 250, display: 'flex', flexDirection: 'column' }}>
          {/* Sidebar header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Terms</span>
            <button
              className="icon-btn"
              onClick={openTermModal}
              title="Add a custom term beyond the board template"
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--slate)', padding: '3px 8px', borderRadius: 6 }}
            >
              <Plus size={13} /> Add
            </button>
          </div>

          {/* Term list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {terms.length === 0 ? (
              <p style={{ color: 'var(--text-2)', fontSize: 12, lineHeight: 1.5 }}>
                No terms yet. Terms are auto-created when the academic year is activated.
              </p>
            ) : (
              terms.map(t => (
                <button key={t.id} onClick={() => setTermId(t.id)} className={`side-menu-item${termId === t.id ? ' active' : ''}`}>
                  <span className="smi-l" style={{ flex: 1 }}>{t.name}</span>
                  {t.weightage != null && <span style={pill}>{Number(t.weightage)}%</span>}
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', minWidth: 16, textAlign: 'right' }}>{t.exam_count}</span>
                  <Trash2 size={13} onClick={(e) => { e.stopPropagation(); delTerm(t.id); }} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
                </button>
              ))
            )}
          </div>
        </nav>

        {/* Exams panel */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {termId == null
            ? <div className="empty">
                <div className="empty-art" style={{ background: 'var(--slate-soft)', color: 'var(--slate)' }}><CalendarRange size={34} /></div>
                <p>Select a term on the left to view and configure its exams</p>
              </div>
            : <ExamsPane termId={termId} classes={classes} onTermCountChanged={() => loadTerms(yearId as number, curriculumId)} />
          }
        </div>
      </div>

      {/* Add term modal */}
      {showTermModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowTermModal(false); }}
        >
          <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 24, width: 420, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800 }}>Add custom term</h3>
            <p style={{ margin: '0 0 20px', fontSize: 12, color: 'var(--text-2)' }}>
              Standard terms are auto-created from the board template. Use this only to add an extra term.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label className="field" style={{ gap: 4 }}>
                <span style={lbl}>Term name</span>
                <input className="input" placeholder="e.g. Term 3" value={tName} onChange={e => setTName(e.target.value)} autoFocus />
              </label>
              <label className="field" style={{ gap: 4 }}>
                <span style={lbl}>Weightage % (optional)</span>
                <input className="input" type="number" placeholder="—" value={tWeight} onChange={e => setTWeight(e.target.value)} />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
              <button className="btn" onClick={() => setShowTermModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addTerm} style={{ gap: 6 }}><Plus size={15} /> Add term</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExamsPane({ termId, classes, onTermCountChanged }: { termId: number; classes: ClassRow[]; onTermCountChanged: () => void }) {
  const [exams, setExams]         = useState<Exam[]>([]);
  const [openExam, setOpenExam]   = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [name, setName]       = useState('');
  const [type, setType]       = useState('formative');
  const [classId, setClassId] = useState<number | ''>('');
  const [weight, setWeight]   = useState('');

  const load = useCallback(() => {
    apiRequest<any>(`${BASE}/exams?term_id=${termId}`).then(r => setExams(r.data || [])).catch(() => {});
  }, [termId]);
  useEffect(load, [load]);

  const openAdd = () => { setName(''); setType('formative'); setClassId(''); setWeight(''); setShowModal(true); };

  const add = async () => {
    if (!name.trim()) return toastMessage('Enter an exam name', 'error');
    try {
      const r = await apiRequest(`${BASE}/exams`, {
        method: 'POST',
        body: JSON.stringify({ term_id_fk: termId, name: name.trim(), exam_type: type, class_id_fk: classId || null, weightage: weight ? Number(weight) : null }),
      });
      if (!r.success) { toastMessage(r.message || 'Could not add exam', 'error'); return; }
      setShowModal(false);
      load(); onTermCountChanged();
      toastMessage('Exam added', 'success');
    } catch { toastMessage('Could not add exam', 'error'); }
  };

  const del = async (id: number) => {
    if (!confirm('Delete this exam?')) return;
    try {
      // apiRequest resolves (doesn't throw) on HTTP error responses — e.g. a
      // 409 when marks already exist under this exam — so success must be
      // checked explicitly or the failed delete looks like it worked and the
      // exam silently stays listed.
      const r = await apiRequest(`${BASE}/exams/${id}`, { method: 'DELETE' });
      if (!r.success) { toastMessage(r.message || 'Could not delete', 'error'); return; }
      if (openExam === id) setOpenExam(null);
      load(); onTermCountChanged();
    } catch { toastMessage('Could not delete', 'error'); }
  };

  const clsLabel = (c: ClassRow) => c.name || c.class_name || c.display_name || `#${c.id}`;

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18 }}>
        <button
          className="btn btn-primary"
          onClick={openAdd}
          style={{ gap: 6 }}
          title="Add a custom exam beyond the standard board template"
        >
          <Plus size={16} /> Add exam
        </button>
      </div>

      {showModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 24, width: 480, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800 }}>Add custom exam</h3>
            <p style={{ margin: '0 0 20px', fontSize: 12, color: 'var(--text-2)' }}>Add an exam outside the standard board template for this term.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label className="field" style={{ gap: 4 }}>
                <span style={lbl}>Exam name</span>
                <input className="input" placeholder="e.g. Special Test" value={name} onChange={e => setName(e.target.value)} autoFocus />
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <label className="field" style={{ gap: 4, flex: 1 }}>
                  <span style={lbl}>Type</span>
                  <select className="select" value={type} onChange={e => setType(e.target.value)}>
                    {EXAM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className="field" style={{ gap: 4, flex: 1 }}>
                  <span style={lbl}>Class</span>
                  <select className="select" value={classId} onChange={e => setClassId(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">All classes</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{clsLabel(c)}</option>)}
                  </select>
                </label>
                <label className="field" style={{ gap: 4, width: 90 }}>
                  <span style={lbl}>Weight %</span>
                  <input className="input" type="number" placeholder="—" value={weight} onChange={e => setWeight(e.target.value)} />
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
              <button className="btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={add} style={{ gap: 6 }}><Plus size={15} /> Add exam</button>
            </div>
          </div>
        </div>
      )}


      {exams.length === 0
        ? <p style={{ color: 'var(--text-2)' }}>No exams in this term yet.</p>
        : exams.map(e => (
          <div key={e.id} style={{ border: '1px solid var(--border)', borderRadius: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' }} onClick={() => setOpenExam(openExam === e.id ? null : e.id)}>
              <ChevronRight size={16} style={{ transform: openExam === e.id ? 'rotate(90deg)' : 'none', transition: 'transform .15s', color: 'var(--text-2)' }} />
              <span style={{ fontWeight: 800 }}>{e.name}</span>
              <ExamTypeBadge type={e.exam_type} label={EXAM_TYPES.find(t => t[0] === e.exam_type)?.[1] || e.exam_type} />
              <span style={pill}>{e.class_name || 'All classes'}</span>
              {e.weightage != null && <span style={pill}>{Number(e.weightage)}%</span>}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <ListChecks size={14} /> {e.subject_count} subj{e.skill_count ? ` · ${e.skill_count} skill` : ''}
              </span>
              <button className="icon-btn" onClick={(ev) => { ev.stopPropagation(); del(e.id); }}><Trash2 size={16} /></button>
            </div>
            {openExam === e.id && <ExamSetup examId={e.id} onChanged={load} />}
          </div>
        ))}
    </div>
  );
}

function ExamSetup({ examId, onChanged }: { examId: number; onChanged: () => void }) {
  const [subjects, setSubjects] = useState<ExamSubject[]>([]);
  const [skills, setSkills]     = useState<ExamSkill[]>([]);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(() => {
    apiRequest<any>(`${BASE}/exams/${examId}/subjects`).then(r => setSubjects(r.data || [])).catch(() => {});
    apiRequest<any>(`${BASE}/exams/${examId}/skills`).then(r => setSkills(r.data || [])).catch(() => {});
  }, [examId]);
  useEffect(load, [load]);

  const patchSubj  = (sid: number, p: Partial<ExamSubject>) => setSubjects(a => a.map(r => r.subject_id === sid ? { ...r, ...p } : r));
  const toggleSkill = (sid: number) => setSkills(a => a.map(r => r.skill_id === sid ? { ...r, included: !r.included } : r));

  const save = async () => {
    setSaving(true);
    try {
      await apiRequest(`${BASE}/exams/${examId}/subjects`, {
        method: 'PUT',
        body: JSON.stringify({ subjects: subjects.map(s => ({ subject_id: s.subject_id, included: s.included, max_marks: s.max_marks === '' ? 100 : Number(s.max_marks) })) }),
      });
      if (skills.length) {
        await apiRequest(`${BASE}/exams/${examId}/skills`, {
          method: 'PUT',
          body: JSON.stringify({ skill_ids: skills.filter(s => s.included).map(s => s.skill_id) }),
        });
      }
      toastMessage('Exam saved', 'success'); load(); onChanged();
    } catch { toastMessage('Could not save', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: 16, background: 'var(--surface-2)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 }}>Subjects in this exam</div>
      {subjects.length === 0
        ? <p style={{ color: 'var(--text-2)', fontSize: 13 }}>No subjects mapped to this class. Add subjects to the class in Academic Setup.</p>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
            {subjects.map(s => (
              <div key={s.subject_id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1, cursor: 'pointer' }}>
                  <input type="checkbox" checked={s.included} onChange={() => patchSubj(s.subject_id, { included: !s.included })} style={{ width: 17, height: 17 }} />
                  <span style={{ fontWeight: 600, fontSize: 13.5, color: s.included ? 'var(--text)' : 'var(--text-3)' }}>{s.name}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: s.included ? 1 : 0.4 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Max marks</span>
                  <input className="input" type="number" value={s.max_marks ?? ''} disabled={!s.included} onChange={e => patchSubj(s.subject_id, { max_marks: e.target.value })} style={{ width: 84, height: 36, textAlign: 'center' }} />
                </label>
              </div>
            ))}
          </div>
        )}

      {skills.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 }}>Skills to grade (optional)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            {skills.map(s => (
              <label key={s.skill_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 999, cursor: 'pointer', background: s.included ? 'var(--slate-soft)' : 'transparent' }}>
                <input type="checkbox" checked={s.included} onChange={() => toggleSkill(s.skill_id)} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</span>
              </label>
            ))}
          </div>
        </>
      )}

      <button className="btn btn-primary" onClick={save} disabled={saving} style={{ gap: 6 }}>
        <Save size={16} /> {saving ? 'Saving…' : 'Save exam setup'}
      </button>
    </div>
  );
}

function flattenClasses(data: any): ClassRow[] {
  return (data?.grouped || []).flatMap((g: any) =>
    (g.classes || []).map((c: any) => ({ id: c.id, name: c.class_label })));
}

const EXAM_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  formative:  { bg: '#e8f5e9', color: '#2e7d32' },
  summative:  { bg: '#e3f2fd', color: '#1565c0' },
  unit_test:  { bg: '#fff3e0', color: '#e65100' },
  mid_term:   { bg: '#fce4ec', color: '#880e4f' },
  final:      { bg: '#ede7f6', color: '#4527a0' },
  practical:  { bg: '#e0f7fa', color: '#00695c' },
  oral:       { bg: '#f3e5f5', color: '#6a1b9a' },
  other:      { bg: 'var(--slate-soft)', color: 'var(--text-2)' },
};

function ExamTypeBadge({ type, label }: { type: string; label: string }) {
  const c = EXAM_TYPE_COLORS[type] ?? EXAM_TYPE_COLORS.other;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: c.bg, color: c.color }}>
      {label}
    </span>
  );
}

const pill: React.CSSProperties = { fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: 'var(--slate-soft)', color: 'var(--text-2)' };
const lbl: React.CSSProperties  = { fontSize: 11, fontWeight: 700, color: 'var(--text-2)' };
